import { TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import type { ClassifierData } from 'types/classifiers.ts';
import { DocumentTemplateMinDto } from 'types/document';
import {
    BulkDebtNotesUpdateDto,
    CreatedDraftsDto,
    CreateDraftInvoicesDto,
    ExistingPrepaymentDto,
    GroupingInvoiceRowUpdateDto,
    InvoiceBillingNotesDto,
    InvoiceData,
    InvoiceDataListItem,
    InvoiceDebtNotesDto,
    InvoiceDebtReminderDto,
    InvoiceDebtReminderScopeType,
    InvoiceExportListItemDto,
    InvoiceMinDto,
    InvoicePayment,
    InvoicePaymentFormDto,
    InvoiceRowBulkUpdateDto,
    InvoiceRowCreateDto,
    InvoiceRowUpdateDto,
    InvoiceSendValidationDto,
    InvoiceStatusUpdateDto,
    InvoicesTotalsDto,
    InvoiceTotals,
    InvoiceUpdateDto,
    MatterAmountDto,
    PaymentDistributionDto,
    PrepaymentDataListItem,
    UpdateCustomizationDto,
    UpdateInvoiceLimitedDto,
    UpdateInvoicePayersDto,
    UpdateInvoiceTemplateDto,
    UpdatePaymentDistributionDto,
    UpdateRoundingAdjustmentsDto,
} from 'types/invoices';
import { objectToQueryParams } from 'utils/linkUtil';
import { QueryParams } from './common/types.ts';
import { APIContactsSlice } from './contacts.ts';
import { APIInvoiceDetailsSlice } from './invoice-details';
import { APIMattersSlice } from './matters';
import { injectCountEndPoint, portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';
import { handleRTKQueryError } from './util/errorHandling';

export interface NetvisorPushResult {
    warnings: string[];
}

/** The backend also answers the id the integration recorded; nothing on screen uses it, so it is not typed here. */
export interface InvoicingSendResult {
    warnings: string[];
}

type TagTypes =
    | 'Invoices'
    | 'InvoicesMin'
    | 'InvoicesCount'
    | 'InvoicesListItem'
    | 'Notifications'
    | 'Timecards'
    | 'TimeRecords'
    | 'InvoiceTotals'
    | 'InvoicesTotals'
    | 'InvoicePrepayments'
    | 'InvoicePaymentDistribution'
    | 'RelatedDebtInvoices'
    | 'HistoryListItems';

export const APIInvoicesSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'Invoices',
            'InvoicesMin',
            'InvoicesCount',
            'InvoicesListItem',
            'Notifications',
            'Timecards',
            'TimeRecords',
            'InvoiceTotals',
            'InvoicesTotals',
            'InvoicePrepayments',
            'InvoicePaymentDistribution',
            'RelatedDebtInvoices',
            'HistoryListItems',
            'InvoiceSendValidation',
            'BillableMatters',
            'BillableMattersCount',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getInvoiceById: builder.query<InvoiceData, string>({
                query: (id) => `/v1/invoices/${id}`,
                providesTags: (_1, _2, arg) => [{ type: 'Invoices', id: arg }],
                keepUnusedDataFor: 0,
            }),
            getInvoiceRestrictedInfo: builder.query<InvoiceData, string>({
                query: (id) => `/v1/invoices/${id}/restricted-info`,
                keepUnusedDataFor: 0,
            }),
            getInvoiceSendValidation: builder.query<InvoiceSendValidationDto[], number[]>({
                query: (invoiceIds) => `/v1/invoices/send-validation?invoiceIds=${invoiceIds.join(',')}`,
                keepUnusedDataFor: 0,
                providesTags: ['InvoiceSendValidation'],
            }),
            getInvoicesWithFilters: accumulatedQueryWithFilters<InvoiceDataListItem>(builder, '/v1/invoices', {
                providesTags: ['InvoicesListItem'],
                keepUnusedDataFor: 0,
            }),
            getMinInvoicesListWithFilters: accumulatedQueryWithFilters<InvoiceMinDto>(builder, '/v1/invoices/min', {
                providesTags: ['InvoicesMin'],
            }),
            getInvoicesCount: injectCountEndPoint(builder, '/v1/invoices', 'InvoicesCount', 0),
            getInvoicesExportList: builder.query<InvoiceExportListItemDto[], QueryParams>({
                query: ({ filter = '', sort = '' }) => ({
                    url: `/v1/invoices/export${objectToQueryParams({ filter, sort })}`,
                    method: 'GET',
                }),
            }),
            getInvoicesTotals: builder.query<InvoicesTotalsDto, { filters?: string }>({
                query: ({ filters = '' }) => {
                    return `/v1/invoices/calculate-totals${objectToQueryParams({
                        filter: filters || undefined,
                    })}`;
                },
                providesTags: ['InvoicesTotals'],
                keepUnusedDataFor: 0,
            }),
            getExistingPrepayments: builder.query<PrepaymentDataListItem[], { id: number; filters: string }>({
                query: ({ id, filters }) =>
                    `/v1/invoices/${id}/prepayments${filters ? objectToQueryParams({ filter: filters }) : ''}`,
                providesTags: (_1, _2, arg) => [{ type: 'InvoicePrepayments', id: arg.id }],
            }),
            updateExistingPrepayments: builder.mutation<void, { id: number; dtoList: ExistingPrepaymentDto[] }>({
                query: ({ id, dtoList }) => ({
                    url: `/v1/invoices/${id}/prepayments`,
                    method: 'PUT',
                    body: dtoList,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'InvoicesListItem',
                              'InvoiceTotals',
                              'InvoiceSendValidation',
                              'InvoicesCount',
                              { type: 'Invoices', id: arg.id },
                              { type: 'InvoicePrepayments', id: arg.id },
                              { type: 'RelatedDebtInvoices', id: arg.id },
                          ]
                        : [
                              'Invoices',
                              'InvoicesListItem',
                              'InvoiceTotals',
                              'InvoiceSendValidation',
                              'InvoicesCount',
                              'InvoicePrepayments',
                              'RelatedDebtInvoices',
                          ],
            }),
            getInvoicePdf: builder.mutation<string, { entityId: string }>({
                query({ entityId }) {
                    return {
                        url: `/v1/invoices/generate/${entityId}`,
                        method: 'GET',
                        responseHandler: async (response) => window.URL.createObjectURL(await response.blob()),
                        cache: 'no-cache',
                    };
                },
            }),
            getInvoiceTemplates: builder.query<DocumentTemplateMinDto[], void>({
                query: () => '/v1/invoices/templates',
            }),
            getInvoiceSpecTemplates: builder.query<DocumentTemplateMinDto[], void>({
                query: () => '/v1/invoices/spec-templates',
            }),
            getInvoiceTotals: builder.query<InvoiceTotals, number>({
                query: (id) => `/v1/invoices/${id}/totals`,
                providesTags: (_1, _2, arg) => [{ type: 'InvoiceTotals', id: arg }],
            }),
            getInvoiceAccountingTypes: builder.query<ClassifierData[], number>({
                query: (id) => `/v1/invoices/${id}/accounting-rule-types`,
            }),
            createDraftInvoices: builder.mutation<CreatedDraftsDto, CreateDraftInvoicesDto>({
                query: (body) => ({
                    url: `/v1/invoices/create-drafts`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: [
                    'Invoices',
                    'InvoicesListItem',
                    'Notifications',
                    'Timecards',
                    'TimeRecords',
                    'BillableMatters',
                    'BillableMattersCount',
                ],
            }),
            createJointInvoice: builder.mutation<InvoiceData, CreateDraftInvoicesDto>({
                query: (body) => ({
                    url: `/v1/invoices`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: [
                    'Invoices',
                    'InvoicesListItem',
                    'Notifications',
                    'Timecards',
                    'TimeRecords',
                    'BillableMatters',
                    'BillableMattersCount',
                ],
            }),
            createCreditInvoice: builder.mutation<InvoiceData, { invoiceId: string }>({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoices/${invoiceId}/create-credit`,
                    method: 'POST',
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'Notifications'],
            }),
            createCorrectionInvoice: builder.mutation<InvoiceData, { invoiceId: string }>({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoices/${invoiceId}/create-correction`,
                    method: 'POST',
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'Notifications'],
            }),
            reissueDebitInvoice: builder.mutation<InvoiceData, { invoiceId: number }>({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoices/${invoiceId}/reissue`,
                    method: 'POST',
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'Notifications'],
            }),
            createPrepaidInvoice: builder.mutation<InvoiceData, MatterAmountDto>({
                query: ({ ...body }) => ({
                    url: `/v1/invoices/create-prepaid`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'Notifications'],
            }),
            createBlankCreditNote: builder.mutation<InvoiceData, { dtoList: MatterAmountDto[] }>({
                query: ({ dtoList }) => ({
                    url: `/v1/invoices/blank-credit-note`,
                    method: 'POST',
                    body: dtoList,
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'Notifications'],
            }),
            createInvoicePayment: builder.mutation<InvoicePayment, InvoicePaymentFormDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/payments`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? [
                              'InvoicesListItem',
                              'Notifications',
                              { type: 'Invoices', id: arg.invoiceId },
                              { type: 'InvoiceTotals', id: arg.invoiceId },
                              { type: 'RelatedDebtInvoices', id: arg.invoiceId },
                          ]
                        : ['InvoicesListItem', 'Notifications', 'Invoices', 'InvoiceTotals', 'RelatedDebtInvoices'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                },
            }),
            deleteInvoicePayment: builder.mutation<boolean, { invoiceId: number; paymentId: number }>({
                query: ({ invoiceId, paymentId }) => ({
                    url: `/v1/invoices/${invoiceId}/payments/${paymentId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? [
                              'InvoicesListItem',
                              { type: 'Invoices', id: arg.invoiceId },
                              { type: 'InvoiceTotals', id: arg.invoiceId },
                              { type: 'RelatedDebtInvoices', id: arg.invoiceId },
                          ]
                        : ['InvoicesListItem', 'Invoices', 'InvoiceTotals', 'RelatedDebtInvoices'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                },
            }),
            deleteInvoice: builder.mutation<
                boolean,
                {
                    invoiceId: number;
                    /** The other copies of the shared-payment group the invoice leaves, whose shares and totals change. */
                    sharedPaymentInvoiceIds?: number[];
                }
            >({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoices/${invoiceId}`,
                    method: 'DELETE',
                }),
                // Only the copies left are refreshed: a query of the deleted invoice that refetched would fail. A single
                // copy left is a regular invoice again and has no payment distribution to read.
                invalidatesTags: (_1, _2, { sharedPaymentInvoiceIds = [] }) => [
                    'InvoicesListItem',
                    'Notifications',
                    'Timecards',
                    'TimeRecords',
                    ...sharedPaymentInvoiceIds.flatMap((id) => [
                        { type: 'Invoices' as const, id },
                        { type: 'InvoiceTotals' as const, id },
                        ...(sharedPaymentInvoiceIds.length > 1
                            ? [{ type: 'InvoicePaymentDistribution' as const, id }]
                            : []),
                    ]),
                ],
            }),
            updateInvoicePayers: builder.mutation<void, UpdateInvoicePayersDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/invoices/${id}/payers`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'Invoices', id: arg.id },
                    { type: 'InvoiceTotals', id: arg.id },
                    { type: 'InvoicePrepayments', id: arg.id },
                    'InvoicesListItem',
                ],
            }),
            getPaymentDistribution: builder.query<PaymentDistributionDto, number>({
                query: (id) => `/v1/invoices/${id}/payment-distribution`,
                providesTags: (_1, _2, arg) => [{ type: 'InvoicePaymentDistribution', id: arg }],
                keepUnusedDataFor: 0,
            }),
            updatePaymentDistribution: builder.mutation<void, UpdatePaymentDistributionDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/invoices/${id}/payment-distribution`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'InvoiceTotals', 'InvoicePaymentDistribution'],
            }),
            splitInvoice: builder.mutation<InvoiceData, { invoiceId: number }>({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoices/${invoiceId}/split`,
                    method: 'PUT',
                }),
                invalidatesTags: ['Invoices', 'InvoicesListItem', 'Notifications'],
            }),
            mergeInvoice: builder.mutation<InvoiceData, { invoiceId: number; invoiceIds: number[] }>({
                query: ({ invoiceId, invoiceIds }) => ({
                    url: `/v1/invoices/${invoiceId}/merge`,
                    method: 'PUT',
                    params: {
                        invoiceIds,
                    },
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', { type: 'Invoices', id: arg.invoiceId }, 'InvoiceTotals']
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
            }),
            recalculateInvoiceDate: builder.mutation<InvoiceData, { invoiceId: number }>({
                query: ({ invoiceId }) => ({
                    url: `/v1/invoices/${invoiceId}/recalculate-date`,
                    method: 'POST',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? [
                              'InvoicesListItem',
                              { type: 'Invoices', id: arg.invoiceId },
                              'InvoiceTotals',
                              'InvoiceSendValidation',
                          ]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals', 'InvoiceSendValidation'],
            }),
            updateInvoice: builder.mutation<InvoiceData, InvoiceUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/invoices/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              'InvoicesListItem',
                              'InvoiceTotals',
                              'InvoiceSendValidation',
                              { type: 'InvoicePrepayments', id: arg.id },
                              { type: 'Invoices', id: arg.id },
                              { type: 'RelatedDebtInvoices', id: arg.id },
                          ]
                        : [
                              'Invoices',
                              'InvoicesListItem',
                              'InvoiceTotals',
                              'InvoiceSendValidation',
                              'InvoicePrepayments',
                              'RelatedDebtInvoices',
                          ],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                },
            }),
            updateInvoiceLimited: builder.mutation<InvoiceData, UpdateInvoiceLimitedDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/invoices/${id}/limited`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            updateInvoiceClientReference: builder.mutation<void, { id: number; value: string | null }>({
                query: ({ id, value }) => ({
                    url: `/v1/invoices/${id}/client-reference`,
                    method: 'PUT',
                    body: { value },
                }),
                invalidatesTags: (_1, _2, arg) => [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'],
            }),
            updateInvoiceTemplate: builder.mutation<InvoiceData, UpdateInvoiceTemplateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/invoices/${id}/template`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            updateInvoiceStatus: builder.mutation<InvoiceUpdateDto, InvoiceStatusUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/invoices/${id}/update-status`,
                    method: 'PUT',
                    body,
                }),
                // Invoice status transitions cascade onto the underlying timecards (e.g. FINISHED → INVOICED),
                // which changes the kind they appear under in the All-time feed. Invalidate TimeRecords too.
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [
                              { type: 'Invoices', id: arg.id },
                              'Notifications',
                              'InvoiceTotals',
                              'InvoicesListItem',
                              'InvoicesCount',
                              'TimeRecords',
                          ]
                        : [
                              'Invoices',
                              'InvoicesListItem',
                              'Notifications',
                              'InvoiceTotals',
                              'InvoicesCount',
                              'TimeRecords',
                          ],
                async onQueryStarted(_payload, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                },
            }),
            sendToExternalApproval: builder.mutation<void, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/invoices/${id}/external-approve`,
                    method: 'POST',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            sendToNetvisor: builder.mutation<NetvisorPushResult, { id: number; attachSpec?: boolean }>({
                query: ({ id, attachSpec }) => ({
                    url: `/v1/invoices/${id}/send-to-accounting`,
                    method: 'POST',
                    body: { attachSpec: attachSpec ?? false },
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            sendToInvoicing: builder.mutation<InvoicingSendResult, { id: number; attachSpec?: boolean }>({
                query: ({ id, attachSpec }) => ({
                    url: `/v1/invoices/${id}/send-to-invoicing`,
                    method: 'POST',
                    body: { attachSpec: attachSpec ?? false },
                }),
                // A failed send refetches too: a gateway error can hide a send that went through.
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            skipKsef: builder.mutation<void, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/invoices/${id}/skip-external-approval`,
                    method: 'POST',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            resetKsef: builder.mutation<void, { id: number }>({
                query: ({ id }) => ({
                    url: `/v1/invoices/${id}/reset-external-events`,
                    method: 'POST',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id ? [{ type: 'Invoices', id: arg.id }, 'InvoicesListItem'] : ['Invoices', 'InvoicesListItem'],
            }),
            updateInvoiceBillingNotes: builder.mutation<void, InvoiceBillingNotesDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/billing-notes`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled;
                        dispatch(APIMattersSlice.util.invalidateTags([{ type: 'Matter', id: arg.matterId ?? 0 }]));
                        dispatch(APIContactsSlice.util.invalidateTags([{ type: 'Party', id: arg.partyId ?? 0 }]));
                    } catch (error) {
                        handleRTKQueryError(error, 'updateInvoiceBillingNotes', arg);
                    }
                },
            }),
            updateInvoiceDownloadButtonsDisabled: builder.mutation<
                void,
                { invoiceId: number; downloadButtonsDisabled: boolean }
            >({
                query: ({ invoiceId, downloadButtonsDisabled }) => ({
                    url: `/v1/invoices/${invoiceId}/download-buttons-disabled`,
                    method: 'PATCH',
                    body: downloadButtonsDisabled,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem'],
            }),
            updateInvoiceDebtNotes: builder.mutation<void, InvoiceDebtNotesDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/debt-notes`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? [
                              'InvoicesListItem',
                              { type: 'Invoices', id: arg.invoiceId },
                              {
                                  type: 'HistoryListItems',
                                  historySelector: {
                                      entityType: 'INVOICE',
                                      entityId: String(arg.invoiceId),
                                      fieldType: 'RECEIVABILITY_TYPE_CODE',
                                  },
                              },
                          ]
                        : ['Invoices', 'InvoicesListItem'],
            }),
            updateBulkInvoiceDebtNotes: builder.mutation<void, BulkDebtNotesUpdateDto>({
                query: (body) => ({
                    url: `/v1/invoices/bulk-debt-notes`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) => {
                    const tags: TagDescription<TagTypes>[] = ['InvoicesListItem'];
                    if (arg.invoiceIds && arg.invoiceIds.length > 0) {
                        arg.invoiceIds.forEach((id) => {
                            tags.push({ type: 'Invoices', id });
                        });
                    }
                    return tags;
                },
            }),
            updateGroupingInvoiceRow: builder.mutation<void, GroupingInvoiceRowUpdateDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/totals`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
            }),
            resetTotals: builder.mutation<
                void,
                { invoiceId: number; matterId: number; sourceType: 'EXPENSE' | 'TIMECARD' }
            >({
                query: ({ invoiceId, matterId, sourceType }) => ({
                    url: `/v1/invoices/${invoiceId}/totals/${matterId}/reset?sourceType=${sourceType}`,
                    method: 'PUT',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
            }),
            updateInvoiceRow: builder.mutation<void, InvoiceRowUpdateDto>({
                query: ({ invoiceId, id, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/invoice-row/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled;
                        dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                        dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                    } catch (error) {
                        handleRTKQueryError(error, 'updateInvoiceRow', arg);
                    }
                },
            }),
            createInvoiceRow: builder.mutation<void, InvoiceRowCreateDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/invoice-row`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
                async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                    try {
                        await queryFulfilled;
                        dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                        dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                    } catch (error) {
                        handleRTKQueryError(error, 'createInvoiceRow', arg);
                    }
                },
            }),
            deleteInvoiceRow: builder.mutation<boolean, { invoiceId: number; invoiceRowId: number }>({
                query: ({ invoiceId, invoiceRowId }) => ({
                    url: `/v1/invoices/${invoiceId}/invoice-row/${invoiceRowId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                },
            }),
            updateInvoiceRows: builder.mutation<
                void,
                { invoiceId: number; matterId: number; dtoList: InvoiceRowBulkUpdateDto[] }
            >({
                query: ({ invoiceId, matterId, dtoList }) => ({
                    url: `/v1/invoices/${invoiceId}/invoice-rows/${matterId}/bulk`,
                    method: 'PUT',
                    body: dtoList,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    await queryFulfilled;
                    dispatch(APIMattersSlice.util.invalidateTags(['MatterSummary']));
                    dispatch(APIInvoiceDetailsSlice.util.invalidateTags(['InvoiceMatters']));
                },
            }),
            customizeInvoice: builder.mutation<void, { invoiceId: number } & UpdateCustomizationDto>({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/customize`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? [
                              'InvoicesListItem',
                              'InvoiceTotals',
                              'InvoicesCount',
                              { type: 'Invoices', id: arg.invoiceId },
                              { type: 'InvoicePrepayments', id: arg.invoiceId },
                              { type: 'RelatedDebtInvoices', id: arg.invoiceId },
                          ]
                        : [
                              'Invoices',
                              'InvoicesListItem',
                              'InvoiceTotals',
                              'InvoicesCount',
                              'InvoicePrepayments',
                              'RelatedDebtInvoices',
                          ],
            }),
            updateInvoiceRoundingAdjustments: builder.mutation<
                void,
                { invoiceId: number } & UpdateRoundingAdjustmentsDto
            >({
                query: ({ invoiceId, ...body }) => ({
                    url: `/v1/invoices/${invoiceId}/totals/rounding-adjustments`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.invoiceId
                        ? ['InvoicesListItem', 'InvoiceTotals', { type: 'Invoices', id: arg.invoiceId }]
                        : ['Invoices', 'InvoicesListItem', 'InvoiceTotals'],
            }),
            getRelatedDebtInvoices: builder.query<
                InvoiceDebtReminderDto[],
                { invoiceId: number; scopeType: InvoiceDebtReminderScopeType; debtReminderType?: string }
            >({
                query: ({ invoiceId, scopeType, debtReminderType }) => {
                    const params = new URLSearchParams();
                    if (debtReminderType) {
                        params.append('debtReminderType', debtReminderType);
                    }
                    const queryString = params.toString();
                    return `/v1/invoices/${invoiceId}/debt-reminder/debt-invoices/${scopeType}${queryString ? `?${queryString}` : ''}`;
                },
                providesTags: (_1, _2, arg) => [{ type: 'RelatedDebtInvoices', id: arg.invoiceId }],
            }),
        }),
        overrideExisting: false,
    });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetInvoiceByIdQuery,
    useGetInvoiceRestrictedInfoQuery,
    useGetInvoiceSendValidationQuery,
    useGetExistingPrepaymentsQuery,
    useGetMinInvoicesListWithFiltersQuery,
    useLazyGetMinInvoicesListWithFiltersQuery,
    useGetInvoicesWithFiltersQuery,
    useGetInvoicesCountQuery,
    useLazyGetInvoicesExportListQuery,
    useGetInvoiceTemplatesQuery,
    useGetInvoiceSpecTemplatesQuery,
    useLazyGetInvoicesWithFiltersQuery,
    useGetInvoicePdfMutation,
    useGetInvoiceTotalsQuery,
    useGetInvoicesTotalsQuery,
    useLazyGetInvoicesTotalsQuery,
    useGetInvoiceAccountingTypesQuery,
    useCreateDraftInvoicesMutation,
    useCreateJointInvoiceMutation,
    useCreateInvoicePaymentMutation,
    useCreateInvoiceRowMutation,
    useDeleteInvoicePaymentMutation,
    useDeleteInvoiceMutation,
    useDeleteInvoiceRowMutation,
    useUpdateInvoiceMutation,
    useUpdateInvoiceLimitedMutation,
    useUpdateInvoiceClientReferenceMutation,
    useUpdateInvoiceTemplateMutation,
    useUpdateGroupingInvoiceRowMutation,
    useUpdateInvoiceRowMutation,
    useUpdateInvoiceStatusMutation,
    useSendToExternalApprovalMutation,
    useSendToNetvisorMutation,
    useSendToInvoicingMutation,
    useSkipKsefMutation,
    useResetKsefMutation,
    useUpdateInvoiceBillingNotesMutation,
    useUpdateInvoiceDownloadButtonsDisabledMutation,
    useUpdateInvoiceDebtNotesMutation,
    useUpdateBulkInvoiceDebtNotesMutation,
    useCreatePrepaidInvoiceMutation,
    useCreateBlankCreditNoteMutation,
    useCreateCreditInvoiceMutation,
    useCreateCorrectionInvoiceMutation,
    useUpdateInvoiceRowsMutation,
    useReissueDebitInvoiceMutation,
    useLazyGetInvoicesCountQuery,
    useSplitInvoiceMutation,
    useMergeInvoiceMutation,
    useResetTotalsMutation,
    useUpdateExistingPrepaymentsMutation,
    useCustomizeInvoiceMutation,
    useLazyGetRelatedDebtInvoicesQuery,
    useRecalculateInvoiceDateMutation,
    useUpdateInvoiceRoundingAdjustmentsMutation,
    useUpdateInvoicePayersMutation,
    useGetPaymentDistributionQuery,
    useUpdatePaymentDistributionMutation,
} = APIInvoicesSlice;
